import { apiClient } from '../api-client'
import type {
  PlansResponse,
  SubscriptionData,
  TransactionsResponse,
  TransactionFilterParams
} from '@/types'
import type {
  CreditBalanceResponse,
  CreditPackagesResponse,
  CreditPricingResponse,
  CreditTransactionsResponse,
  CreditCheckResult,
  CostPreviewResult,
  CostPreviewOperation,
  StorageResponse,
  CreditTopUpPackagesResponse,
} from '@/types/credits'

// ===========================================
// CREDIT SYSTEM API (v2) - NEW
// ===========================================

/**
 * Get current credit balance for workspace
 */
export async function getCreditBalance(): Promise<CreditBalanceResponse> {
  const response = await apiClient.get<CreditBalanceResponse>('/api/v2/credits/balance')
  return response.data
}

/**
 * Get all available credit packages
 */
export async function getCreditPackages(): Promise<CreditPackagesResponse> {
  const response = await apiClient.get<CreditPackagesResponse>('/api/v2/credits/packages')
  return response.data
}

/**
 * Get credit pricing for all operations
 */
export async function getCreditPricing(): Promise<CreditPricingResponse> {
  const response = await apiClient.get<CreditPricingResponse>('/api/v2/credits/pricing')
  return response.data
}

/**
 * Get credit transaction history
 */
export async function getCreditTransactions(
  limit: number = 50,
  offset: number = 0
): Promise<CreditTransactionsResponse> {
  const response = await apiClient.get<CreditTransactionsResponse>(
    `/api/v2/credits/transactions?limit=${limit}&offset=${offset}`
  )
  return response.data
}

/**
 * Check if workspace has enough credits for operation
 */
export async function checkCredits(
  operationType: string,
  count: number = 1
): Promise<CreditCheckResult> {
  const response = await apiClient.post<CreditCheckResult>('/api/v2/credits/check', {
    operation_type: operationType,
    count,
  })
  return response.data
}

/**
 * Get cost preview for multiple operations
 */
export async function getCostPreview(
  operations: CostPreviewOperation[]
): Promise<CostPreviewResult> {
  const response = await apiClient.post<CostPreviewResult>('/api/v2/credits/cost-preview', {
    operations,
  })
  return response.data
}

/**
 * Get storage usage for workspace
 */
export async function getStorageUsage(): Promise<StorageResponse> {
  const response = await apiClient.get<StorageResponse>('/api/v2/credits/storage')
  return response.data
}

/**
 * Get available credit top-up packages (one-time purchases)
 */
export async function getCreditTopUpPackages(): Promise<CreditTopUpPackagesResponse> {
  const response = await apiClient.get<CreditTopUpPackagesResponse>(
    '/api/v2/credits/topup-packages'
  )
  return response.data
}

// ===========================================
// LEGACY API (v1) - DEPRECATED
// ===========================================

/**
 * @deprecated Use getCreditPackages() instead
 * Holt alle verfuegbaren Subscription-Plaene (API v1)
 */
export async function getPlans(): Promise<PlansResponse> {
  const response = await apiClient.get<PlansResponse>('/api/v1/plans')
  return response.data
}

/**
 * @deprecated Use getCreditBalance() instead
 * Holt aktuelle Workspace-Subscription und Bildpreis (API v1)
 */
export async function getSubscription(): Promise<SubscriptionData> {
  const response = await apiClient.get<SubscriptionData>('/api/v1/subscription')
  return response.data
}

/**
 * @deprecated Use getCreditTransactions() instead
 * Holt Transaktionen mit Filter und Pagination (API v1)
 */
export async function getTransactions(params?: TransactionFilterParams): Promise<TransactionsResponse> {
  const searchParams = new URLSearchParams()

  if (params?.filter) {
    searchParams.append('filter', params.filter)
  }
  if (params?.from) {
    searchParams.append('from', params.from)
  }
  if (params?.to) {
    searchParams.append('to', params.to)
  }
  if (params?.page) {
    searchParams.append('page', params.page.toString())
  }
  if (params?.per_page) {
    searchParams.append('per_page', params.per_page.toString())
  }

  const queryString = searchParams.toString()
  const url = queryString ? `/api/v1/transactions?${queryString}` : '/api/v1/transactions'

  const response = await apiClient.get<TransactionsResponse>(url)
  return response.data
}





